import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import {
  ORDER_STATE,
  accountIds,
  noticeToast,
  describeExposure,
  exposureOf,
  isFlat,
  exitScopeConfirmed,
  useConnection,
  type BookIdentity,
  useSettings,
  type ConfirmChoice,
  useTrading,
  type AccountExposure,
  type ExitScope,
} from "@helm/core";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { pushToast } from "../components/Toasts";
import { onTradingFrame } from "../session";
import { flattenAllConfirmed } from "./actions";
import "./trading.css";
import "./domControls.css";

/**
 * Hosts for the trading UI's transient surfaces, mounted once in the desktop shell:
 * the order-confirmation popover (one-click off, SPEC §9.8), the Flatten ALL confirm dialog
 * (with its own remembered preference), exit progress until the gateway reports flat (owner 2026-09-27), and toasts
 * for order outcomes the user must see (rejected, UNKNOWN → reconciling, not placed) and for every
 * Warn / Error gateway notice plus the order flow's Info outcomes (`noticeToast`, @helm/core).
 */

// ---- tiny stores ----------------------------------------------------------------------------

function store<T>(initial: T): {
  get: () => T;
  set: (v: T) => void;
  subscribe: (l: () => void) => () => void;
} {
  let value = initial;
  const ls = new Set<() => void>();
  return {
    get: () => value,
    set: (v) => {
      value = v;
      for (const l of ls) l();
    },
    subscribe: (l) => {
      ls.add(l);
      return () => {
        ls.delete(l);
      };
    },
  };
}

// ---- order confirmation popover ---------------------------------------------------------------

/** Where to anchor the confirmation: the click position in its window (pop-outs included). */
export type Anchor = { x: number; y: number; doc: Document };

/**
 * A warning confirm instead of an order confirm: asked whatever the one-click setting, with its
 * own title, button and note (AB-3: a protective stop moved through the market).
 */
export type ConfirmWarning = { title: string; confirmLabel: string; note: string };
type ConfirmAsk = { words: string; anchor: Anchor | null; large?: boolean; warning?: ConfirmWarning };
type ConfirmReq = ConfirmAsk & { resolve: (choice: ConfirmChoice) => void };
const confirmStore = store<ConfirmReq | null>(null);

/**
 * Ask the user to confirm an order in words. A second request while one is open is refused.
 * `large`: a large quantity (OB4), asked even with one-click on; that popover also offers
 * "Don't ask again for large orders" (owner 2026-09-30), reported in `dontAskAgain`.
 */
export function confirmOrderChoice(req: ConfirmAsk): Promise<ConfirmChoice> {
  if (confirmStore.get() !== null) return Promise.resolve({ ok: false, dontAskAgain: false });
  return new Promise((resolve) => {
    confirmStore.set({ ...req, resolve });
  });
}

/** {@link confirmOrderChoice} when only yes / no matters (no "Don't ask again" box). */
export async function confirmOrder(req: { words: string; anchor: Anchor | null }): Promise<boolean> {
  return (await confirmOrderChoice(req)).ok;
}

/** Anchor from a mouse/pointer event. */
export function anchorOf(e: { clientX: number; clientY: number; currentTarget: EventTarget | null }): Anchor {
  const el = e.currentTarget as { ownerDocument?: Document } | null;
  return { x: e.clientX, y: e.clientY, doc: el?.ownerDocument ?? document };
}

/** Anchor below an element (keyboard activation, hotkeys). */
export function anchorBelow(el: Element | null): Anchor | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.bottom, doc: el.ownerDocument };
}

function OrderConfirm({ req }: { req: ConfirmReq }): React.JSX.Element {
  const ref = useRef<HTMLButtonElement | null>(null);
  const large = req.large === true;
  const warning = req.warning ?? null;
  // "Don't ask again for large orders": saved only if the order is confirmed (actions.ts).
  const [dontAsk, setDontAsk] = useState(false);
  const done = (ok: boolean): void => {
    confirmStore.set(null);
    req.resolve({ ok, dontAskAgain: large && dontAsk });
  };
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const doc = req.anchor?.doc ?? document;
  const win = doc.defaultView ?? window;
  const w = 280;
  const x = req.anchor
    ? Math.min(Math.max(8, req.anchor.x - w / 2), win.innerWidth - w - 8)
    : win.innerWidth / 2 - w / 2;
  const h = large || warning ? 150 : 110;
  const y = req.anchor ? Math.min(req.anchor.y + 8, win.innerHeight - h) : win.innerHeight / 3;
  return createPortal(
    <div
      className={warning ? "order-confirm warning" : large ? "order-confirm large" : "order-confirm"}
      role="alertdialog"
      aria-label={warning ? warning.title : large ? "Confirm large order" : "Confirm order"}
      style={{ left: x, top: y, width: w }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          done(false);
        } else if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          done(true);
        }
      }}
    >
      {warning && <div className="order-confirm-title">{warning.title}</div>}
      <div className="order-confirm-words">{req.words}</div>
      {large && (
        <label className="order-confirm-dont-ask">
          <input
            type="checkbox"
            checked={dontAsk}
            onChange={(e) => {
              setDontAsk(e.target.checked);
            }}
          />
          Don&apos;t ask again for large orders
        </label>
      )}
      <div className="order-confirm-actions">
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            done(false);
          }}
        >
          Cancel <kbd>Esc</kbd>
        </button>
        <button
          ref={ref}
          type="button"
          className={warning ? "btn btn-sm btn-danger" : "btn btn-sm btn-primary"}
          onClick={() => {
            done(true);
          }}
        >
          {warning?.confirmLabel ?? "Confirm"} <kbd>Enter</kbd>
        </button>
      </div>
      <div className="order-confirm-note muted">
        {warning
          ? warning.note
          : large
            ? "Large quantity: confirmed even with one-click on (threshold: Risk Settings → Confirm orders above; turn back on: Settings → Confirm large orders)"
            : "One-click trading is off (Settings)"}
      </div>
    </div>,
    doc.body,
  );
}

// ---- Flatten ALL accounts ---------------------------------------------------------------------

const flattenAllStore = store(false);

export function isFlattenAllOpen(): boolean {
  return flattenAllStore.get();
}

function closeFlattenAll(): void {
  flattenAllStore.set(false);
}

export function openFlattenAll(): void {
  flattenAllStore.set(true);
}

function FlattenAllDialog(): React.JSX.Element {
  const s = useTrading((t) => t);
  const privacy = useSettings((x) => x.settings.privacy);
  const [busy, setBusy] = useState(false);
  const [dontAsk, setDontAsk] = useState(false);
  const ids = accountIds(s);
  const exposure: AccountExposure[] = exposureOf(s, { accounts: ids, symbol: null });
  return (
    <ConfirmDialog
      title="Exit All — all accounts"
      className="flatten-all-confirm"
      confirmLabel="Exit all accounts"
      busy={busy}
      onCancel={closeFlattenAll}
      onConfirm={() => {
        setBusy(true);
        void flattenAllConfirmed(ids, dontAsk).finally(() => {
          setBusy(false);
          closeFlattenAll();
        });
      }}
    >
      <p>
        Cancels every working order, then closes every position at market, on each of these {ids.length}{" "}
        accounts:
      </p>
      <ul className="flatten-all-list" aria-label="Accounts to flatten">
        {exposure.map((e) => (
          <li key={e.accountId} data-account={e.accountId}>
            <strong>{privacy ? "***" : e.accountId}</strong>
            {": "}
            {e.positions.length === 0
              ? "no open positions"
              : e.positions
                  .map((p) => `${p.symbol} ${p.netQty > 0 ? "+" : "−"}${String(Math.abs(p.netQty))}`)
                  .join(", ")}
            {" · "}
            {e.workingOrders} working order{e.workingOrders === 1 ? "" : "s"}
          </li>
        ))}
      </ul>
      <label className="order-confirm-dont-ask">
        <input
          type="checkbox"
          checked={dontAsk}
          disabled={busy}
          onChange={(e) => {
            setDontAsk(e.target.checked);
          }}
        />
        Don&apos;t ask again for Exit All
      </label>
      <p className="muted">Turn it back on in Settings › Order defaults › Ask before Exit All.</p>
    </ConfirmDialog>
  );
}

// ---- exit progress ----------------------------------------------------------------------------

/** ⚙ How long an exit may take before the leftovers are reported (gateway waits ≤ 1 s for cancels). */
export const EXIT_DEADLINE_MS = 5000;

type ExitWatch = { id: number; scope: ExitScope; label: string; deadline: number; identity: BookIdentity };
const watchStore = store<readonly ExitWatch[]>([]);
let watchSeq = 0;

/** Follow an accepted exit using its pre-submit identity, or report what is left at the deadline. */
export function watchExit(scope: ExitScope, label: string, identity: BookIdentity): void {
  watchStore.set([
    ...watchStore.get().filter((w) => w.label !== label),
    {
      id: ++watchSeq,
      scope,
      label,
      deadline: Date.now() + EXIT_DEADLINE_MS,
      identity,
    },
  ]);
}

function ExitProgress(): React.JSX.Element | null {
  const watches = useSyncExternalStore(watchStore.subscribe, watchStore.get);
  const s = useTrading((t) => t);
  const connected = useConnection((c) => c.status === "connected");
  const replayReady = useConnection((c) => c.financeReady);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (watches.length === 0) return;
    const t = setInterval(() => {
      setNow(Date.now());
    }, 250);
    return () => {
      clearInterval(t);
    };
  }, [watches.length]);
  useEffect(() => {
    if (watches.length === 0) return;
    const keep: ExitWatch[] = [];
    for (const w of watches) {
      const e = exposureOf(s, w.scope);
      const confirmed = exitScopeConfirmed(s, w.scope.accounts, w.identity, connected, replayReady);
      if (confirmed && isFlat(e)) {
        pushToast(
          { tone: "success", title: w.label, body: "Flat: no open positions or working orders left" },
          { ttlMs: 4000 },
        );
      } else if (now >= w.deadline) {
        pushToast(
          {
            tone: "danger",
            title: confirmed
              ? `${w.label}: not flat after ${String(EXIT_DEADLINE_MS / 1000)} s`
              : `${w.label}: flat status unconfirmed`,
            body: confirmed
              ? `Still open: ${describeExposure(e)}. Check Positions and Orders; exit again if needed.`
              : "Broker confirmation is unavailable. An empty list does not mean flat. Check connection, Positions and Orders.",
          },
          { assertive: true, ttlMs: 15_000 },
        );
      } else {
        keep.push(w);
      }
    }
    if (keep.length !== watches.length) watchStore.set(keep);
  }, [watches, s, now, connected, replayReady]);
  if (watches.length === 0) return null;
  return (
    <div className="exit-progress" role="status" aria-live="polite">
      {watches.map((w) => (
        <div key={w.id} className="exit-progress-row">
          <span className="spinner" aria-hidden="true" />
          <span>
            {w.label}…{" "}
            {exitScopeConfirmed(s, w.scope.accounts, w.identity, connected, replayReady)
              ? describeExposure(exposureOf(s, w.scope)) || "confirming"
              : "waiting for broker confirmation"}
          </span>
        </div>
      ))}
    </div>
  );
}

// ---- order outcomes and gateway notices --------------------------------------------------------

/** A proto enum value as a plain number. */
const num = (v: number): number => v;

/** Market-data notices (core stream/notices.ts): the chart shows them, not the trading feedback. */
const MARKET_NOTICES: ReadonlySet<string> = new Set([
  "MD_GAP",
  "MD_SYMBOL_LIMIT",
  "TOO_MANY_REQUESTS",
  "HISTORY_RANGE_TOO_LARGE",
  "NO_MARKET_DATA",
  "SUBSCRIBE_FAILED",
]);

/**
 * Toasts for order outcomes and for every Warn / Error gateway notice (launch audit B2: nothing
 * critical is filtered by code; Error ones stay until dismissed). Mount once per window (desktop
 * and mobile shells).
 */
export function useOrderFeedback(): void {
  useEffect(() => {
    // Last state seen per order: toasts fire on live transitions only, not on replayed history.
    const seen = new Map<string, number>();
    for (const [id, o] of useTrading.getState().orders) seen.set(id, o.state);
    // Frames replayed after a reconnect or page load describe the past: no toast for them.
    const since = BigInt(Date.now() - 10_000) * 1_000_000n;
    const offStore = useTrading.subscribe((st) => {
      for (const [id, o] of st.orders) {
        const prev = seen.get(id);
        if (prev === o.state) continue;
        seen.set(id, o.state);
        if (o.updatedNs !== 0n && o.updatedNs < since) continue;
        const what = `${num(o.side) === 1 ? "Buy" : "Sell"} ${String(o.qty)} ${o.symbol}`;
        const state = num(o.state);
        if (state === ORDER_STATE.REJECTED) {
          pushToast(
            { tone: "danger", title: `Rejected: ${what}`, body: o.rejectReason || "Rejected by Rithmic" },
            { assertive: true, ttlMs: 10_000 },
          );
        } else if (state === ORDER_STATE.UNKNOWN) {
          pushToast(
            {
              tone: "warn",
              title: `Reconciling: ${what}`,
              body: "No confirmation yet. TradePrizm is checking open orders and history; it will never resend. Same-side orders on this contract wait until it resolves.",
            },
            { assertive: true, ttlMs: 12_000 },
          );
        } else if (state === ORDER_STATE.NOT_PLACED && prev !== undefined) {
          pushToast(
            {
              tone: "warn",
              title: `Not placed: ${what}`,
              body: "No trace at the exchange after reconciling. Nothing was resent.",
            },
            { ttlMs: 10_000 },
          );
        }
      }
    });
    // Frames up to the first Welcome's last_seq were replayed from the gateway's ring: they
    // predate this page, so their toasts expire instead of persisting.
    let historyUpTo: bigint | null = null;
    const offFrames = onTradingFrame((env) => {
      if (env.body.case === "welcome") {
        historyUpTo ??= env.body.value.lastSeq;
        return;
      }
      if (env.body.case !== "notice") return;
      const n = env.body.value;
      // Answers to this window's own requests (market data, history) are handled by the requester.
      if (env.reqId !== "" || MARKET_NOTICES.has(n.code)) return;
      const t = noticeToast(
        { level: num(n.level), code: n.code, text: n.text },
        historyUpTo !== null && env.seq > 0n && env.seq <= historyUpTo,
      );
      if (!t) return;
      pushToast(
        { tone: t.tone, title: t.title, body: t.body },
        { assertive: t.assertive, persist: t.persist, ttlMs: t.ttlMs },
      );
    });
    return () => {
      offStore();
      offFrames();
    };
  }, []);
}

/** Mount once per window (desktop and mobile shells). */
export function TradingHosts(): React.JSX.Element {
  const confirm = useSyncExternalStore(confirmStore.subscribe, confirmStore.get);
  const flattenAll = useSyncExternalStore(flattenAllStore.subscribe, flattenAllStore.get);
  useOrderFeedback();
  return (
    <>
      {confirm && <OrderConfirm key={confirm.words} req={confirm} />}
      {flattenAll && <FlattenAllDialog />}
      <ExitProgress />
    </>
  );
}
